import React, { useEffect, useRef } from "react";
import { cn } from "utils";
import { DayPickerProps } from "react-day-picker";

export interface TimePickerProps {
  date?: Date;
  onChange?: (date: Date) => void;
  className?: string;
  disabled?: DayPickerProps["disabled"];
}

const hoursRange = Array.from({ length: 24 }, (_, i) => i);
const minutesRange = Array.from({ length: 60 }, (_, i) => i);
const secondsRange = Array.from({ length: 60 }, (_, i) => i);

// Helper function to check if a date is disabled
const isTimeDisabled = (
  testDate: Date,
  disabled?: DayPickerProps["disabled"],
): boolean => {
  if (!disabled) {
    return false;
  }

  // If disabled is boolean
  if (typeof disabled === "boolean") {
    return disabled;
  }

  // If disabled is an array of rules
  if (Array.isArray(disabled)) {
    return disabled.some((rule: any) => {
      // Exact date match
      if (rule instanceof Date) {
        return testDate.getTime() === rule.getTime();
      }

      // Before rule - disable dates before this time
      if ("before" in rule) {
        return testDate < rule.before;
      }

      // After rule - disable dates after this time
      if ("after" in rule) {
        return testDate > rule.after;
      }

      // DayOfWeek rule (not applicable for time picker)
      if ("dayOfWeek" in rule) {
        return false;
      }

      return false;
    });
  }

  return false;
};

const TimeColumn: React.FC<{
  options: number[];
  value: number;
  onChange: (val: number) => void;
  type: "hour" | "minute" | "second";
  baseDate: Date;
  disabled?: DayPickerProps["disabled"];
}> = ({ options, value, onChange, type, baseDate, disabled }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      const selectedElement = containerRef.current.querySelector(
        `[data-value="${value}"]`,
      ) as HTMLElement;
      if (selectedElement) {
        // 使用 scrollIntoView 确保元素平滑滚动到容器顶部
        selectedElement.scrollIntoView({
          block: "start",
          behavior: "smooth",
        });
      }
    }
  }, [value]);

  const isOptionDisabled = (option: number): boolean => {
    if (!disabled) {
      return false;
    }

    // Create a test date with the current option
    const testDate = new Date(baseDate);
    if (type === "hour") {
      testDate.setHours(option);
    } else if (type === "minute") {
      testDate.setMinutes(option);
    } else if (type === "second") {
      testDate.setSeconds(option);
    }

    // For time-based disabled rules, we need special handling
    if (Array.isArray(disabled)) {
      return disabled.some((rule: any) => {
        // After rule - disable times after this specific datetime
        if ("after" in rule && rule.after instanceof Date) {
          // Compare the full datetime
          return testDate > rule.after;
        }

        // Before rule - disable times before this specific datetime
        if ("before" in rule && rule.before instanceof Date) {
          return testDate < rule.before;
        }

        // For other rules, use the original logic
        return isTimeDisabled(testDate, [rule]);
      });
    }

    return isTimeDisabled(testDate, disabled);
  };

  return (
    <div
      ref={containerRef}
      className="flex flex-col overflow-y-auto h-[260px] w-[60px] no-scrollbar max-md:flex-1"
      style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
    >
      {options.map((option) => {
        const isDisabled = isOptionDisabled(option);
        return (
          <div
            data-openreplay-label={`DatePicker · TimePicker · div | ${option.toString().padStart(2, "0")}`}
            key={option}
            data-value={option}
            onClick={() => !isDisabled && onChange(option)}
            className={cn(
              "shrink-0 h-8 flex items-center justify-center transition-colors rounded mx-1",
              "body_s",
              isDisabled
                ? "cursor-not-allowed opacity-50 text-content_quaternary"
                : "cursor-pointer text-content_secondary hover:bg-background_white_overlay5",
              value === option &&
                !isDisabled &&
                "text-content_brand_primary bg-background_brand_overlay10 font-bold",
              value === option &&
                isDisabled &&
                "text-content_quaternary bg-background_white_overlay5",
            )}
          >
            {option.toString().padStart(2, "0")}
          </div>
        );
      })}
      {/* 底部预留空间，确保最后一个选项可以滚动到顶部 */}
      <div className="h-[228px] shrink-0" />
    </div>
  );
};

export const TimePicker: React.FC<TimePickerProps> = ({
  date,
  onChange,
  className,
  disabled,
}) => {
  const safeDate = date || new Date();
  // If no date is provided, default to 00:00:00
  const hours = date ? safeDate.getHours() : 0;
  const minutes = date ? safeDate.getMinutes() : 0;
  const seconds = date ? safeDate.getSeconds() : 0;

  const handleTimeChange = (
    type: "hour" | "minute" | "second",
    val: number,
  ) => {
    if (!onChange) {
      return;
    }
    const newDate = new Date(safeDate);
    if (type === "hour") {
      newDate.setHours(val);
    }
    if (type === "minute") {
      newDate.setMinutes(val);
    }
    if (type === "second") {
      newDate.setSeconds(val);
    }
    onChange(newDate);
  };
  const H = "H";
  const M = "M";
  const S = "S";

  return (
    <div className={cn("flex flex-col", className)}>
      <div className="flex border-b border-border_white_transparent10 pb-2 mb-2 justify-around">
        <span className="body_xs text-content_secondary">{H}</span>
        <span className="body_xs text-content_secondary">{M}</span>
        <span className="body_xs text-content_secondary">{S}</span>
      </div>
      <div className="flex">
        <TimeColumn
          options={hoursRange}
          value={hours}
          onChange={(val) => handleTimeChange("hour", val)}
          type="hour"
          baseDate={safeDate}
          disabled={disabled}
        />
        <TimeColumn
          options={minutesRange}
          value={minutes}
          onChange={(val) => handleTimeChange("minute", val)}
          type="minute"
          baseDate={safeDate}
          disabled={disabled}
        />
        <TimeColumn
          options={secondsRange}
          value={seconds}
          onChange={(val) => handleTimeChange("second", val)}
          type="second"
          baseDate={safeDate}
          disabled={disabled}
        />
      </div>
    </div>
  );
};
